/**
 * Returns true when an error represents a client-side connectivity failure
 * (offline, DNS failure, network change) rather than an actionable application
 * error.
 *
 * These errors are not worth reporting to RUM: they are caused by the user's
 * network, not the app. Worse, while the client is offline the RUM web client
 * cannot reach cognito-identity to fetch credentials, so every recorded error
 * triggers a failing credential fetch (and an uncaught "Failed to retrieve
 * Cognito identity" rejection). Skipping them avoids that storm entirely.
 */
export const isConnectivityError = (error: unknown): boolean => {
  // Fully offline — any failure here is connectivity, not the app.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true
  }

  // Per the Fetch spec, fetch() rejects a network-layer failure (DNS, offline,
  // connection reset, network change) with a TypeError in every browser,
  // regardless of the message wording ("Failed to fetch", "Load failed", ...).
  // Apollo forwards that rejection unchanged to the error link.
  if (error instanceof TypeError) {
    return true
  }

  // Amplify's subscription transport tags its connectivity errors name
  // "NetworkError" (a plain object, not an Error instance).
  return (error as { name?: string } | null | undefined)?.name === "NetworkError"
}
